import { useEffect, useState } from 'react';

import {
  HomeIcon,
  LockIcon,
  LogOutIcon,
  SettingsIcon,
  TicketIcon,
} from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import { authState } from '../../../services/state/auth-state.jsx';
import { cn } from '../../../utils/cn.js';
import { CATALYST_USER_ROLES } from '../../../utils/constants.js';
import { getInitials } from '../../../utils/misc.js';

import Logo from '../../../assets/img/mar-shopping-landscape.svg';

import JoinDoorIcon from '../../icons/join-door.jsx';
import { Avatar, AvatarFallback } from '../../ui/avatar.jsx';
import { Button } from '../../ui/button.jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu.jsx';

export default function Header({ headerUserSection, setScreen }) {
  const { user, isAdmin, logout } = useSnapshot(authState);
  const location = useLocation();

  const [open, setOpen] = useState(false);

  const { pathname } = location;

  useEffect(() => {
    if (open) {
      setOpen(false);
    }
  }, [location]);

  const links = [
    {
      to: '/portal',
      icon: HomeIcon,
      label: 'Home',
      activeCondition: (pathname) =>
        pathname === '/portal' ||
        (pathname.includes('/portal') &&
          !pathname.includes('/portal/coupons') &&
          !pathname.includes('/portal/account')),
    },
    {
      to: '/portal/coupons',
      icon: TicketIcon,
      label: 'Cupões',
      activeCondition: (pathname) =>
        pathname === '/portal/coupons' || pathname.includes('/portal/coupons'),
    },
  ];

  return (
    <nav className="top-0 z-[25] w-full flex-col bg-white px-4 py-8 text-foreground dark:bg-muted/95 lg:!flex lg:justify-between lg:px-28">
      <div className="flex flex-row items-start justify-between gap-x-4 sm:items-center sm:gap-y-0">
        <div className="flex flex-row gap-6">
          <NavLink
            to="/"
            className="group relative flex items-center rounded-xl"
          >
            <img src={Logo} alt="Mar Shopping Logo" className="h-10 w-auto" />
          </NavLink>

          <div className="gap flex gap-4">
            {user && user?.details?.role === CATALYST_USER_ROLES.customer && (
              <>
                {links.map((link, index) => {
                  const Icon = link.icon;

                  return (
                    <NavLink
                      key={index}
                      to={link.to}
                      className={cn(
                        'group relative flex h-10 items-center gap-2 rounded-xl px-3 py-3 outline-1 outline-secondary transition-all hover:bg-secondary lg:px-4',
                        link.activeCondition(pathname)
                          ? 'outline outline-portal-foreground'
                          : 'hover:outline',
                      )}
                    >
                      <Icon className="size-5" />
                      <span className="hidden lg:block">{link.label}</span>
                    </NavLink>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {headerUserSection && user ? (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="size-10 rounded-xl">
                <Avatar className="size-10 rounded-xl border border-input">
                  <AvatarFallback className="size-10 rounded-xl dark:bg-muted/40">
                    {getInitials(user.fullName)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex items-center gap-2.5 px-1 py-1.5 text-left text-sm">
                  <Avatar className="size-10 rounded-xl border border-input">
                    <AvatarFallback className="rounded-xl dark:bg-muted/40">
                      {getInitials(user.fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user.fullName}
                    </span>
                    <span className="truncate text-xs">
                      {user.details.role}
                    </span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                {isAdmin && (
                  <Link to="/backoffice">
                    <DropdownMenuItem>
                      <LockIcon className="mr-2 size-4" />
                      Backoffice
                    </DropdownMenuItem>
                  </Link>
                )}

                {user?.details?.role === CATALYST_USER_ROLES.customer && (
                  <Link to="/portal/account">
                    <DropdownMenuItem>
                      <SettingsIcon className="mr-2 size-4" />
                      Definições
                    </DropdownMenuItem>
                  </Link>
                )}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => logout()}>
                <LogOutIcon className="mr-2 size-3.5" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link to="/auth/login">
            <Button variant="portal">
              <JoinDoorIcon />
              Entrar
            </Button>
          </Link>
        )}
      </div>
    </nav>
  );
}
